<template>
    <div class="tm-scope tp-job">
        <div class="tp-job__header">
            <h4 class="fw-bold tp-job__title">{{ job ? job.name : '' }}</h4>
            <p v-if="job && job.market_description" class="tp-job__desc">{{ job.market_description }}</p>
            <p v-if="job && job.description" class="tp-job__desc">{{ job.description }}</p>
            <div v-if="job && job.also_known_as.length" class="mt-4">
                <h5 class="fw-bold">Alternative labels</h5>
                <div class="tp-tag-row mt-2">
                    <div v-for="label in job.also_known_as.slice(0, 20)" :key="label" class="tm-tag">{{ label }}</div>
                </div>
            </div>
        </div>

        <div v-if="job" class="tp-job__bar">
            <div v-if="tabs.length > 1" class="tm-segmented" role="tablist" aria-label="Role sections">
                <button v-for="t in tabs" :key="t.key" type="button" role="tab" class="tm-segmented__btn"
                        :class="{ active: tab === t.key }" :aria-selected="tab === t.key" @click="tab = t.key">
                    {{ t.label }}
                </button>
            </div>
            <LocationSelect v-model="location" class="tp-job__loc" @change="load" />
        </div>

        <!-- Skills: the original role-details layout -->
        <template v-if="job && tab === 'skills'">
            <div v-for="sec in skillSections" :key="sec.title" class="tp-job-section">
                <div class="row">
                    <div class="col-lg-6">
                        <h4>{{ sec.title }}</h4>
                        <div v-for="item in sec.items" :key="item.skill_id" class="skill-row">
                            <span class="skill-name">{{ item.name }}</span>
                            <BatteryComponent :importanceItem="item.importance" :type="barType(item)" />
                        </div>
                    </div>
                    <div v-if="sec.items.length" class="col-lg-6">
                        <VueApexCharts type="radar" :height="radarHeight(sec)" :options="radar(sec)" :series="[{ name: sec.title, data: sec.items.map(i => Math.round(i.importance * 100)) }]" />
                    </div>
                </div>
            </div>
        </template>

        <div v-if="job && tab === 'responsibilities'" class="tp-job-section">
            <h4>Responsibilities</h4>
            <div v-for="r in job.responsibilities" :key="r.name" class="skill-row">
                <span class="skill-name skill-name--wide">{{ r.name }}</span>
                <BatteryComponent :importanceItem="r.share" type="fill-neutral" />
            </div>
        </div>

        <div v-if="job && tab === 'salary'" class="tp-job-section">
            <h4>Salary</h4>
            <div class="skill-row tp-salary tp-salary--head">
                <span class="skill-name">Location</span>
                <span class="tp-salary__median">Median</span>
                <span class="tp-salary__range">Middle half</span>
                <span class="tp-salary__n">Postings</span>
            </div>
            <div v-for="s in salaries" :key="s.location + s.currency" class="skill-row tp-salary">
                <span class="skill-name">{{ s.location }}</span>
                <span class="tp-salary__median tm-mono">{{ money(s.median, s.currency) }}</span>
                <span class="tp-salary__range tm-mono">{{ money(s.p25, s.currency) }} – {{ money(s.p75, s.currency) }}</span>
                <span class="tp-salary__n tm-mono">{{ s.n }}</span>
            </div>
        </div>

        <template v-if="job && tab === 'education'">
            <div class="tp-job-section">
                <div class="row">
                    <div class="col-lg-6" v-if="experience.length">
                        <h4>Experience</h4>
                        <div v-for="e in experience" :key="e.name" class="skill-row">
                            <span class="skill-name">{{ e.name }}</span>
                            <BatteryComponent :importanceItem="e.share" type="fill-neutral" />
                        </div>
                    </div>
                    <div class="col-lg-6" v-if="education.length">
                        <h4>Education</h4>
                        <div v-for="e in education" :key="e.name" class="skill-row">
                            <span class="skill-name">{{ e.name }}</span>
                            <BatteryComponent :importanceItem="e.share" type="fill-neutral" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="tp-job-section" v-if="fields.length || job.certificates.length">
                <div class="row">
                    <div class="col-lg-6" v-if="fields.length">
                        <h4>Fields of study</h4>
                        <div v-for="f in fields" :key="f.name" class="skill-row">
                            <span class="skill-name">{{ f.name }}</span>
                            <BatteryComponent :importanceItem="f.share" type="fill-neutral" />
                        </div>
                    </div>
                    <div class="col-lg-6" v-if="job.certificates.length">
                        <h4>Certificates</h4>
                        <div v-for="c in job.certificates" :key="c.name" class="skill-row">
                            <span class="skill-name">{{ c.name }}</span>
                            <BatteryComponent :importanceItem="c.share" type="fill-neutral" />
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div v-if="job && tab === 'paths'" class="tp-job-section">
            <div class="row">
                <div class="col-lg-6" v-if="job.transitions.length">
                    <h4>Career transitions</h4>
                    <router-link v-for="t in job.transitions" :key="t.occupation_id" class="skill-row tp-link"
                                 :to="{ name: detailRoute, params: { id: t.occupation_id } }">
                        <span class="skill-name">{{ t.name }}</span>
                        <BatteryComponent :importanceItem="t.skill_similarity" type="fill-neutral" />
                    </router-link>
                </div>
                <div class="col-lg-6" v-if="job.demand_by_location.length">
                    <h4>Where the jobs are</h4>
                    <div v-for="d in job.demand_by_location" :key="d.name" class="skill-row">
                        <span class="skill-name">{{ d.name }}</span>
                        <BatteryComponent :importanceItem="d.postings / demandTotal" type="fill-neutral" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import VueApexCharts from 'vue3-apexcharts'
import BatteryComponent from '@/components/BatteryComponent.vue'
import LocationSelect from '@/components/LocationSelect.vue'
import { CareerApiService } from '@/services/CareerApiService'
import { OccupationApiService } from '@/services/OccupationApiService'

const HIDE = ['None stated', 'Unspecified', 'Experienced (unspecified)']

// One page for every occupation. compare=true (career tool): bars show which skills the
// user's CV has (have/gap colours); compare=false (public catalogue): neutral bars only.
export default defineComponent({
    name: 'RoleDetails',
    components: { VueApexCharts, BatteryComponent, LocationSelect },
    props: { compare: { type: Boolean, default: false } },
    setup(props) {
        const route = useRoute()
        const job = ref(null)
        const userSkillIds = ref(new Set())
        const tab = ref('skills')
        const location = ref(route.query.location || localStorage.getItem('topment_location') || 'Global')
        const detailRoute = computed(() => (props.compare ? 'dashboard-job-detail' : 'occupation'))

        async function load() {
            const id = route.params.id
            try { localStorage.setItem('topment_location', location.value) } catch (e) { /* private mode */ }
            const res = await OccupationApiService.Occupation(id, location.value)
            job.value = res.data
            document.title = `${res.data.name.charAt(0).toUpperCase()}${res.data.name.slice(1)} | Topment`
            if (props.compare && localStorage.getItem('cv_id')) {
                const gap = await CareerApiService.MissingSkills(id, localStorage.getItem('cv_id'), location.value).catch(() => null)
                userSkillIds.value = new Set(((gap && gap.data.user_skills) || []).map(s => s.skill_id))
            }
            if (!tabs.value.some(t => t.key === tab.value)) tab.value = 'skills'
        }

        // The role's top 10 by importance; in compare mode your skills first, then missing ones.
        function ordered(list) {
            const items = (list || []).slice(0, 10).map(s => ({ ...s, importance: Math.round((s.importance || 0) * 100) / 100 }))
            if (!props.compare) return items
            const has = s => userSkillIds.value.has(s.skill_id)
            return [...items.filter(has), ...items.filter(s => !has(s))]
        }
        const skillSections = computed(() => job.value ? [
            { title: 'Core Skills', items: ordered(job.value.skills) },
            { title: 'Hidden Skills', items: ordered(job.value.hidden_skills) },
            { title: 'Legendary Skills', items: ordered(job.value.transferable_skills) },
        ] : [])
        const salaries = computed(() => {
            if (!job.value) return []
            if (job.value.salary) return [{ location: job.value.location, ...job.value.salary }]
            return job.value.salary_by_location || []
        })
        const experience = computed(() => (job.value && job.value.experience ? job.value.experience.buckets.filter(e => !HIDE.includes(e.name)) : []))
        const education = computed(() => (job.value && job.value.education ? job.value.education.levels.filter(e => !HIDE.includes(e.name)) : []))
        const fields = computed(() => (job.value && job.value.education ? job.value.education.fields : []))
        const demandTotal = computed(() => Math.max(...(job.value ? job.value.demand_by_location.map(d => d.postings) : [1]), 1))
        const tabs = computed(() => {
            const j = job.value
            if (!j) return []
            return [
                { key: 'skills', label: 'Skills', show: true },
                { key: 'responsibilities', label: 'Responsibilities', show: j.responsibilities.length },
                { key: 'salary', label: 'Salary', show: salaries.value.length },
                { key: 'education', label: 'Education & experience', show: experience.value.length || education.value.length },
                { key: 'paths', label: 'Career paths', show: j.transitions.length || j.demand_by_location.length },
            ].filter(t => t.show)
        })

        // Axis labels wrap to two short lines (ellipsis past that) so the long skill names
        // don't shrink the polygon; full names and values are in the list beside it.
        function axisLabel(name, width = 16) {
            const words = name.replace(/\s*\(.*\)$/, '').split(' ')
            words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1)
            let first = words.shift()
            while (words.length && (first + ' ' + words[0]).length <= width) first += ' ' + words.shift()
            if (!words.length) return first
            let second = words.shift()
            while (words.length && (second + ' ' + words[0]).length <= width) second += ' ' + words.shift()
            if (words.length || second.length > width) second = second.slice(0, width - 1) + '…'
            return [first, second]
        }

        // The chart is as tall as the list beside it (title + ~35px per row); the polygon fills
        // that height, leaving room for the two-line labels around it.
        const radarHeight = sec => Math.max(320, 60 + sec.items.length * 36)

        // Same radar look the role details always had: hairline chrome, brand blue series.
        function radar(sec) {
            return {
                chart: { type: 'radar', background: 'transparent', toolbar: { show: false },
                         fontFamily: "'Plus Jakarta Sans', sans-serif" },
                stroke: { colors: ['#1668E3'], width: 2 },
                fill: { opacity: 0.18, colors: ['#1668E3'] },
                markers: { colors: ['#1668E3'], size: 3 },
                plotOptions: { radar: { size: Math.min(radarHeight(sec) / 2 - 40, 132),
                                        polygons: { strokeColors: '#E3E9EF', connectorColors: '#E3E9EF' } } },
                responsive: [{ breakpoint: 576, options: { plotOptions: { radar: { size: 95 } } } }],
                yaxis: { show: false, min: 0, max: 100, tickAmount: 5 },
                xaxis: { categories: sec.items.map(i => axisLabel(i.name)), labels: { style: { colors: Array(sec.items.length).fill('#5A7184'), fontSize: '12px' } } },
                tooltip: { x: { formatter: (_, { dataPointIndex }) => sec.items[dataPointIndex].name } },
            }
        }

        onMounted(() => {
            load()
        })
        watch(() => route.params.id, (id, old) => { if (id && id !== old) { tab.value = 'skills'; load() } })

        return {
            job, tab, tabs, location, skillSections, salaries, experience, education, fields, demandTotal,
            detailRoute, load, radar, radarHeight,
            barType: item => (!props.compare ? 'fill-neutral' : userSkillIds.value.has(item.skill_id) ? 'fill' : 'fill-red'),
            money: (v, cur) => new Intl.NumberFormat('en', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(v),
        }
    },
})
</script>

<style scoped>
.tp-job { max-width: 1100px; margin: 0 auto; padding: 40px 16px 64px; }
.tp-job__header, .tp-job-section {
    padding: 28px 32px;
    border-radius: var(--tm-radius-md);
    background: var(--tm-surface);
    border: 1px solid var(--tm-border);
    margin-bottom: 24px;
}
.tp-job__title::first-letter { text-transform: uppercase; }
.tp-job__bar { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 24px; }
.tp-job__bar .tm-segmented { flex-wrap: wrap; }
.tp-job__loc { margin-left: auto; }
.tp-job__desc { margin-top: 12px; color: var(--tm-ink-muted); }
.tp-tag-row { display: flex; flex-wrap: wrap; gap: 8px; }
.tp-job-section h4 { margin-bottom: 12px; }
.skill-row {
    display: flex; align-items: center; gap: 12px; padding: 7px 0;
    border-bottom: 1px solid var(--tm-border);
}
.skill-name {
    flex: 0 0 45%; max-width: 45%; font-size: 14px; font-weight: 500; color: var(--tm-ink-soft);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.skill-name::first-letter { text-transform: uppercase; }
.skill-name--wide { flex-basis: 60%; max-width: 60%; }
.tp-link { color: inherit; text-decoration: none; }
.tp-link:hover .skill-name { color: var(--tm-accent); }
.tp-salary__median { flex: 1; color: var(--tm-ink); font-weight: 600; }
.tp-salary__range { flex: 1; color: var(--tm-ink-muted); font-size: 13px; }
.tp-salary__n { flex: 0 0 64px; text-align: right; color: var(--tm-ink-faint); font-size: 12px; }
.tp-salary--head span { color: var(--tm-ink-faint); font-size: 12px; font-weight: 600; }
@media (max-width: 640px) {
    .tp-job__header, .tp-job-section { padding: 20px 18px; }
    .skill-name, .skill-name--wide { flex-basis: 50%; max-width: 50%; }
    .tp-salary__range { display: none; }
}
</style>
